import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a product hotspot dialog.
 *
 * The trigger is rendered as a native <a href="..."> in the template.
 * On MOBILE / TOUCH: the browser handles the tap natively — no JS needed.
 * On DESKTOP: JS intercepts the click to open the hover dialog instead.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLAnchorElement|HTMLButtonElement} trigger
 * @property {HTMLAnchorElement} productLink
 *
 * @extends Component<Refs>
 */
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {(() => void) | null} */
  #pointerenterHandler = null;

  /** @type {number | null} */
  timer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Set up desktop-only listeners: hover opens dialog, click toggles it.
   * On desktop the trigger's default <a> navigation is prevented so the
   * dialog opens instead. On mobile we never call this, so the <a> tag
   * navigates natively without any JS involvement.
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    // Hover: open dialog after short delay
    const pointerenterHandler = (e) => {
  // Guard against Safari's "ghost hover" pointerenter on touch taps.
  if (e.pointerType === 'touch') return;
  if (dialog.open) return;
  this.timer = setTimeout(() => this.showDialog(), 120);
  trigger.addEventListener('pointerleave', this.#handlePointerLeave);
};
    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);

    // Click: prevent the <a> navigating and toggle dialog instead
    trigger.addEventListener('click', this.#handleDesktopClick);
  }

  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    trigger.removeEventListener('click', this.#handleDesktopClick);

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Respond to viewport changes between mobile and desktop. */
  #handleBreakpointChange = () => {
  this.#removeDesktopListeners();
  const { dialog } = this.refs;
  const isMobile = window.innerWidth <= 749;

  // Force-hide the dialog directly via inline !important style.
  // This works regardless of any CSS media query or feature-detection
  // quirks — it's driven purely by the actual measured screen width.
  if (isMobile) {
    dialog.style.setProperty('display', 'none', 'important');
  } else {
    dialog.style.removeProperty('display');
  }

  if (!isMobile) {
    this.#setupDesktopListeners();
  }
};

  /**
   * Desktop-only click handler attached to the trigger <a>.
   * Prevents navigation and toggles the dialog instead.
   * @param {MouseEvent} e
   */
  #handleDesktopClick = (e) => {
  if (e.pointerType === 'touch' || isTouchDevice() || isMobileBreakpoint()) {
    return; // let the <a> navigate natively to the product page
  }
  e.preventDefault();
  if (this.refs.dialog.open) {
    this.closeDialog();
  } else {
    this.showDialog();
  }
};

  /** Measure the dialog and set its data-placement attribute. */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;
    if (!hotspotsContainer) return;

    const BUTTON_GAP    = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP     = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect   = trigger.getBoundingClientRect();

    // Measure dialog invisibly
    dialog.style.visibility = 'hidden';
    dialog.style.display    = 'block';
    dialog.style.transform  = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft   = triggerRect.left   - containerRect.left;
    const buttonRight  = triggerRect.right  - containerRect.left;
    const buttonTop    = triggerRect.top    - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft  = buttonLeft - CONTAINER_GAP;

    let x = 'right';
    if      (spaceRight >= dialogWidth + BUTTON_GAP) x = 'right';
    else if (spaceLeft  >= dialogWidth + BUTTON_GAP) x = 'left';
    else                                              x = 'center';

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY   = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY   = buttonBottom;
        y = 'top';
        if      (dialogStartY < CONTAINER_GAP)                             verticalOffset = CONTAINER_GAP - dialogStartY;
        else if (dialogEndY > containerRect.height - CONTAINER_GAP)        verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
      } else {
        if (dialogStartY < CONTAINER_GAP) { verticalOffset = CONTAINER_GAP - dialogStartY; y = 'bottom'; }
      }
    } else {
      if      (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) y = 'bottom';
      else if (buttonTop >= dialogHeight + TOTAL_GAP)                           y = 'top';
      else                                                                       y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * @param {PointerEvent} e
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog  = e.target === dialog;
    const isGoingToDialog  = e.relatedTarget === dialog || (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  // ─── Public API (used by template on:click binding) ──────────────────────

  /**
   * Kept for backwards-compat with any theme framework on:click bindings,
   * but the real logic is now split between #handleDesktopClick (desktop)
   * and the native <a> tag (mobile). This is a no-op safe fallback.
   * @param {MouseEvent} _e
   */
  handleHotspotClick = (_e) => {
    // No-op: desktop clicks are handled by #handleDesktopClick (attached in
    // #setupDesktopListeners). Mobile clicks are handled by the browser via
    // the native <a href> — no JS needed.
  };

  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  // ─── Dialog lifecycle ────────────────────────────────────────────────────

  showDialog = async () => {
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
  };

  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  lightDismissMouse = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) this.closeDialog();
  };

  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);